'use client'

import React, { memo } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import clsx from 'clsx'
import { getSectionName } from '@/constants/breadcrumbs'
import { BreadcrumbList, ListItem, WithContext } from 'schema-dts'

export type BreadcrumbItemType = {
  name: string
  url?: string
  isLast?: boolean
}

export type BreadcrumbsProps = {
  items: BreadcrumbItemType[]
  className?: string
}

/**
 * JSONLDを生成する
 */
const generateSchemaJsonLd = (
  items: BreadcrumbItemType[],
  pathname: string
): WithContext<BreadcrumbList> => {
  // ベースURL
  const baseUrl = 'https://lawgue.com'

  const itemListElement = items.map((item, index) => {
    // URLの処理
    let itemPath = item.url || ''

    // 末尾のスラッシュを正規化（/voice と /voice/ を同じ扱いにする）
    if (itemPath && !itemPath.startsWith('http')) {
      itemPath =
        itemPath.endsWith('/') && itemPath !== '/'
          ? itemPath.slice(0, -1)
          : itemPath
    }

    // 相対パスの場合は、ベースURLを追加
    const itemUrl = itemPath.startsWith('http')
      ? itemPath
      : `${baseUrl}${itemPath}`

    // 最後の項目の場合は現在のパスを使用
    const finalUrl = item.isLast ? `${baseUrl}${pathname}` : itemUrl

    return {
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@id': finalUrl,
        name: item.name
      }
    } as ListItem
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement
  } as WithContext<BreadcrumbList>
}

/**
 * パンくずリスト用コンポーネント
 */
export const Breadcrumbs = memo(
  ({ items, className = '' }: BreadcrumbsProps) => {
    const pathname = usePathname()

    if (!items || items.length === 0) return null

    const jsonLd = generateSchemaJsonLd(items, pathname || '')

    return (
      <>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
        <div className="bg-[#35478C] w-full flex items-center justify-center flex-row px-6 h-10">
          <nav
            aria-label="パンくずリスト"
            className={clsx('w-full lg:max-w-[1200px] lg:mx-auto', className)}
          >
            <ol className="flex items-center text-xs w-full">
              {items.map((item, index) => {
                const isLast = index === items.length - 1

                return (
                  <li
                    key={`${item.name}-${index}`}
                    className={clsx('flex items-center min-w-0', {
                      'text-gray-600 shrink-0': !isLast,
                      'font-medium flex-1': isLast
                    })}
                  >
                    {!isLast && item.url ? (
                      <>
                        <Link
                          href={item.url}
                          className="text-white underline hover:opacity-80"
                        >
                          {item.name}
                        </Link>
                        <span className="mx-1 shrink-0">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                            className="-rotate-90"
                          >
                            <path
                              d="M5.84375 9.59323L11.5008 15.2502L17.1577 9.59323L16.4508 8.88623L11.5008 13.8362L6.55075 8.88623L5.84375 9.59323Z"
                              fill="#B0B0B0"
                            ></path>
                          </svg>
                        </span>
                      </>
                    ) : (
                      <span className="truncate text-white">{item.name}</span>
                    )}
                  </li>
                )
              })}
            </ol>
          </nav>
        </div>
      </>
    )
  }
)

Breadcrumbs.displayName = 'Breadcrumbs'

/**
 * 現在のページパスからパンくずリストのアイテムを自動生成する
 */
export const useBreadcrumbs = () => {
  const pathname = usePathname()

  // パスの最初の空文字を除去して配列化
  const paths = pathname?.split('/').filter(Boolean) || []

  // 基本のホームアイテム
  const breadcrumbItems: BreadcrumbItemType[] = [
    {
      name: 'ホーム',
      url: '/'
    }
  ]

  // パスごとに適切な名前とURLを生成
  let currentPath = ''
  paths.forEach((path, index) => {
    currentPath += `/${path}`
    const isLast = index === paths.length - 1

    // パス名を読みやすい表示名に変換
    // sectionNames.tsで定義済みの名前を使用
    const displayName = getSectionName(path)

    breadcrumbItems.push({
      name: displayName,
      url: isLast ? undefined : currentPath,
      isLast
    })
  })

  return breadcrumbItems
}

/**
 * ページタイトルを含めたパンくずリストコンポーネント
 */
export const PageBreadcrumbs = memo(
  ({
    pageTitle,
    className = ''
  }: {
    pageTitle?: string
    className?: string
  }) => {
    let items = useBreadcrumbs()

    // ページタイトルが提供された場合、最後のアイテムを置き換える
    if (pageTitle && items.length > 0) {
      const lastIndex = items.length - 1
      items = [...items.slice(0, lastIndex), { name: pageTitle, isLast: true }]
    }

    return <Breadcrumbs items={items} className={className} />
  }
)

PageBreadcrumbs.displayName = 'PageBreadcrumbs'
